// Auto Sync Engine
// Processes sync queue when online, handles conflicts, manages retry

import { getSyncQueue, markSynced, clearSyncedItems, logConflict, getLocalPhotos } from './offlineDB';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('session_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

// Process all pending items in the sync queue
export const processSync = async (onProgress) => {
  const queue = await getSyncQueue();
  if (queue.length === 0) return { synced: 0, failed: 0, total: 0 };

  let synced = 0, failed = 0;

  // Sort: reports first, then equipment, then photos, then status
  const sorted = queue.sort((a, b) => {
    const priority = { task_update: 1, equipment_usage: 2, photo_upload: 3, status_update: 4 };
    return (priority[a.type] || 5) - (priority[b.type] || 5);
  });

  for (const item of sorted) {
    try {
      if (onProgress) onProgress({ current: synced + failed + 1, total: sorted.length, item });

      if (item.type === 'task_update') {
        const res = await fetch(`${API}/imft/tasks/${item.task_id}`, {
          method: 'PUT', headers: getHeaders(), body: JSON.stringify(item.data)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // Conflict detection
          if (res.status === 409) {
            logConflict({ type: 'task_update', task_id: item.task_id, local_data: item.data, server_error: err });
          }
          throw new Error(err.detail || `${res.status}`);
        }
      } else if (item.type === 'equipment_usage') {
        // Equipment already deducted on task completion server-side
      } else if (item.type === 'power_test') {
        await fetch(`${API}/imft/power-test`, {
          method: 'POST', headers: getHeaders(), body: JSON.stringify(item.data)
        });
      }

      await markSynced(item.id);
      synced++;
    } catch (err) {
      console.error(`Sync failed for ${item.type}:`, err);
      failed++;
    }
  }

  // Clean up synced items
  await clearSyncedItems();

  return { synced, failed, total: sorted.length };
};

// Check if online
export const isOnline = () => navigator.onLine;

// Auto-sync with interval
let syncInterval = null;

export const startAutoSync = (onSyncComplete) => {
  if (syncInterval) clearInterval(syncInterval);

  // Sync every 30 seconds when online
  syncInterval = setInterval(async () => {
    if (!isOnline()) return;
    const queue = await getSyncQueue();
    if (queue.length === 0) return;

    try {
      const result = await processSync();
      if (onSyncComplete) onSyncComplete(result);
    } catch {}
  }, 30000);

  // Also sync on online event
  window.addEventListener('online', async () => {
    const result = await processSync();
    if (onSyncComplete) onSyncComplete(result);
  });
};

export const stopAutoSync = () => {
  if (syncInterval) { clearInterval(syncInterval); syncInterval = null; }
};
